import React, { useMemo } from "react";
import axios, { AxiosResponse } from "axios";
import moment from 'moment';
import { AlertColor, Button, Checkbox, FormControlLabel, Link, MenuItem, Paper, TextField, useMediaQuery} from "@mui/material";
import { DataGridPro, GridCellParams, GridColumns, GridRowId, GridToolbarContainer } from '@mui/x-data-grid-pro';
import Title from "../../component/Title";
import { BaseControl, DropDownComboControl, DropDownComboOption } from "../../component/GenericFilter";
import { globalTheme } from "../../../common/theme";
import { useConstructor } from "../../../common/UseConstructor";
import { CustomTablePagination } from "../../component/CustomPagination";
import { PurchaseOrderFilingListItem } from "./PurchaseOrderAccessories";
import { useAppSelector } from "../../../common/hooks";
import { RootState } from "../../../common/Store";
import { CommonProps } from "../../component/MainContents";
import { getUserPreferences, renewToken } from "../../../services/AuthService";
import { SnackBarMessage, SnackBarMessageProps } from "../../component/SnackBarMessage";
import { useNavigate } from "react-router-dom";
import { FormatNumber } from "../../../common/Formatter";
import { RegionCombo } from "../regions/RegionAccessories";
import { BranchInfo } from "../branches/BranchAccessories";
import { alignProperty } from "@mui/material/styles/cssUtils";
import { Print } from "@mui/icons-material";
import { PurchaseOrderFilingListViewer, PurchaseOrderFilingListViewerHandle } from "./PurchaseOrderFilingListViewer";

export function PurchaseOrderFilingList(props: CommonProps) {
	const [loading, setLoading] = React.useState(true);
	const [hasError, setHasError] = React.useState(false);
	const [regionComboData, setRegionComboData] = React.useState<DropDownComboOption[]>([]);

    const isDebug = useAppSelector((state: RootState) => state.menuToggle.debugMode);

    const viewerModalRef = React.useRef<PurchaseOrderFilingListViewerHandle>(null);

	const navigate = useNavigate();

    const style = { ...globalTheme() };

    const tinyScreen = useMediaQuery('(max-width:1366px)');
    const smallScreen = useMediaQuery('(max-width:1920px)');
    const normalScreen = useMediaQuery('(max-width:2560px)');

    const [controls, setControls] = React.useState<BaseControl[]>([]); //For filtering

	const [branchInfo, setBranchInfo] = React.useState<BranchInfo[]>([]);

    const [selectionModel, setSelectionModel] = React.useState<GridRowId[]>([]);

    const [searchData, setSearchData] = React.useState<any>({
        contains: true,
        searchText: "",
        customer: 0,
        endUser: 0,
        pageSize: 25,
    });

    const checkboxLabelStyle = {
        border: "none",
        width: '80px',
        height: '28px',
        paddingTop: 0,
        paddingLeft: 8
    };

    const selectedData = React.useRef<PurchaseOrderFilingListItem[]>([]);
	
	const [selectedRegion, setSelectedRegion] = React.useState<string>("");
	
	const [fileNo, setFileNo] = React.useState<number>(0);
	
	const [originalPoNoFrom, setOriginalPoNoFrom] = React.useState<string>("");
	const [poNoFrom, setPoNoFrom] = React.useState<string>("");
	const [poNoTo, setPoNoTo] = React.useState<string>("");
	const [printed, setPrinted] = React.useState<boolean>(false);

	const [currentPage, setCurrentPage] = React.useState<number>(0);

    //Dialogs and snacks
    const [snackSettings, setSnackSettings] =
      React.useState<SnackBarMessageProps>({
        open: false,
        message: "",
        subMessage: "",
        severity: undefined,
        onClose: () => setSnackSettings({ open: false, message: "", subMessage: "", severity: undefined, onClose: () => {}})
      });

    function showSnackBar(message: string, subMessage: string, colour?: AlertColor) {
      setSnackSettings({
        open: true,
        message: message,
        subMessage: subMessage,
        severity: colour,
        onClose: () => setSnackSettings({ open: false, message: "", subMessage: "", severity: undefined, onClose: () => {}})
      });
    }

	function LoadData(poNoFrom: string, poNoTo: string, printed: boolean) {
		setLoading(true);
		
		const params = { poNoFrom: poNoFrom == "" ? "-" : poNoFrom, poNoTo: poNoTo == "" ? "-" : poNoTo, printed: printed};

		axios.get<PurchaseOrderFilingListItem[]>('Api/V1/PurchaseOrder/FilingList', {
			params,
			headers: {
				'Content-Encoding': 'gzip'
			}
		})
			.then((result) => {
				let rowId = 0;
				result.data.forEach(i => {
					i.id = rowId;
					rowId++;
				});

				setGridData(result.data);

				if (result.data.length > 0)
				{
					if (printed)
					{
						selectedData.current = result.data;
						setSelectionModel(() => result.data.map((r) => r.id));
						setFileNo(result.data[0].printIndex);
					}
					else
					{
						setSelectionModel([]);
						if (originalPoNoFrom.length==0)
						{
							setFileNo(branchInfo.find(x => x.region == selectedRegion)?.printIndex ?? 0);
						}
					}
				}

				if (originalPoNoFrom.length>0)
				{
					setPoNoFrom(originalPoNoFrom);
					setOriginalPoNoFrom("");
				}
				setLoading(false);
			},
				(error) => {
					setGridData([]);
					setLoading(false);
					console.log(error);
					showSnackBar(error.toString(), error?.response?.data?.errors[0], "error");
				}
			)
			.catch((error) => {
				console.log(error);
				showSnackBar(error.toString(), error?.response?.data?.errors[0], "error");
			});
	}

	useConstructor(() => Initialise())
    function Initialise() {
        setLoading(true);
		renewToken();

		let endUsersDropDownOption: DropDownComboOption[] = [];
		props.endUsers.map(e => endUsersDropDownOption.push({ name: e.endUserName, value: e.endUserId.toString() }));
		setControls([
			{
				key: 'customer',
				title: 'Customer',
				dropdownOptions: props.customers
			} as DropDownComboControl,
			{
				key: 'endUser',
				title: 'End User',
				dropdownOptions: endUsersDropDownOption
			} as DropDownComboControl
		]);

		axios.all([
			axios.get<any[]>(`Api/V1/Region/Combo`, {
				headers: {
					'Content-Encoding': 'gzip'
				}
			}),                
			axios.get<BranchInfo[]>(`Api/V1/Branch`, {
				headers: {
					'Content-Encoding': 'gzip'
				}
			})
		]).then(axios.spread((...responses) => {
			const regionComboResult: AxiosResponse<RegionCombo[]> = responses[0];
			const branchInfoResult: AxiosResponse<BranchInfo[]> = responses[1];

			let loadedRegions: DropDownComboOption[] = [];
			setBranchInfo(branchInfoResult.data);
			branchInfoResult.data.map(b => {
				if (regionComboResult.data.find(r => r.regionName === b.region))
					loadedRegions.push({ name: b.companyName1, value: b.region });
			});
			setRegionComboData(loadedRegions);

			let preferences = getUserPreferences();
			if (preferences)
			{
				setSelectedRegion(preferences.defaultRegion);
			}
			setFileNo(branchInfoResult.data.find(b => b.region === preferences?.defaultRegion)?.printIndex ?? 0);

			setLoading(false);

			LoadData("-", "-", printed);
		})).catch((error) => {
			setHasError(true);
			setLoading(false);
			console.log(error);
			showSnackBar(error.toString(), error?.response?.data?.errors[0], "error");
		})

    }

	//Grid columns
	const columns: GridColumns = [
		{ field: 'poNo', headerName: 'PO No', flex: 2.2, type: 'string' },
		{ field: 'customerName', headerName: 'Customer', flex: 5, type: 'string' },
		{ field: 'endUser', headerName: 'End User', flex: 5, type: 'string' },
		{ field: 'createDate', headerName: 'Date', flex: 1.5, 
			renderCell: (params: GridCellParams) => (
				<TextField variant="standard" value={moment(params.value).format('YYYY/MM/DD')} InputProps={{ disableUnderline: true }}/>
			)
		},
		{ field: 'staff', headerName: 'Sales', flex: 2, type: 'string' },
		{ field: 'createdBy', headerName: 'Created By', flex: 2, type: 'string' },
		{ field: 'printIndex', headerName: 'File No.', flex: 1.5, type: 'string' }
	];

	const [gridData, setGridData] = React.useState<PurchaseOrderFilingListItem[]>([]);

	function CustomToolbar() {
		return (
			<GridToolbarContainer style={{ display: 'flex' }}>
				<Button variant="contained" size="small" color="secondary" style={{ margin: "2px" }} startIcon={<Print />} onClick={() => PrintCurrentPage()} disabled={DisablePrintButton(printed)}>Print Current Page</Button>
				<div style={{ width: 10 }}></div>
				<Button variant="contained" size="small" color="secondary" style={{ margin: "2px" }} startIcon={<Print />} onClick={() => PrintAll()} disabled={DisablePrintButton(printed)}>Print All</Button>				
				<div style={{ width: 10 }}></div>
				<Button variant="contained" size="small" color="secondary" style={{ margin: "2px" }} startIcon={<Print />} onClick={() => RePrint()} disabled={DisablePrintButton(!printed)}>Re-Print</Button>				
			</GridToolbarContainer>
		);
	}

	function DisablePrintButton(enableButton: boolean) : boolean {
		return (enableButton || gridData.length == 0)
	}

	function PrintFilingRecord(poNos: string, reprint: boolean) {
		setLoading(true);
		let params = { poNos: poNos, printIndex: fileNo, reprint: reprint};

		axios.get<PurchaseOrderFilingListItem[]>('Api/V1/PurchaseOrder/UpdatePrintIndex', {
			params,
			headers: {
				'Content-Encoding': 'gzip'
			}
		})
			.then((result) => {
				let rowId = 0;
				result.data.forEach(i => {
					i.id = rowId;
					rowId++;
				});

				setGridData(result.data);
				selectedData.current = result.data;
				setSelectionModel(() => result.data.map((r) => r.id));
				let arrayPoNos = poNos.split(',');
				setPoNoFrom(arrayPoNos[0]);
				setPoNoTo(arrayPoNos[arrayPoNos.length - 1]);
				Preview();
				setLoading(false);
			},
				(error) => {
					setGridData([]);
					setLoading(false);
					console.log(error);
					showSnackBar(error.toString(), error?.response?.data?.errors[0], "error");
				}
			)
			.catch((error) => {
				console.log(error);
				showSnackBar(error.toString(), error?.response?.data?.errors[0], "error");
			});		
	}

    function Preview() {
		viewerModalRef.current?.Initialise(selectedRegion, fileNo);
	}
  
	function PrintCurrentPage() {
		if (gridData.length==0)
			return;

		let poNos = "";
		for (let i = 0; i < gridData.length; i++)
		{
			if (i >= currentPage * searchData.pageSize && i < (currentPage + 1 ) * searchData.pageSize)
			{
				poNos += gridData[i].poNo + ",";
			}
		}
		setOriginalPoNoFrom(poNoFrom);
		PrintFilingRecord(poNos.substring(0, poNos.length - 1), false);
	}

	function PrintAll() {
		let poNos = Array.prototype.map.call(gridData, s => s.poNo).toString();
		PrintFilingRecord(poNos, false);
	}

	function RePrint() {
		let poNos = "";
		selectedData.current.forEach(x => {
				poNos += x.poNo + ",";
		});
		poNos = poNos.substring(0, poNos.length - 1);
		if (poNos == "")
		{
			poNos = "-";
		}
		PrintFilingRecord(poNos, true);
	}

	function RegionChanged(region: string) {
		axios.get<BranchInfo[]>(`Api/V1/Branch`, {
			headers: {
			  'Content-Encoding': 'gzip'
			}
		  })
			.then((result) => {
				setBranchInfo(result.data);
				let branchSelected: BranchInfo | undefined = result.data.find(b => b.region === region);
				if (branchSelected) {
					setFileNo(result.data.find(b => b.region === region)?.printIndex ?? 0);
					setSelectedRegion(region);
				}
				if (originalPoNoFrom.length>0)
				{
					setPoNoFrom(originalPoNoFrom);
					setPoNoTo("");
					setOriginalPoNoFrom("")
					LoadData(originalPoNoFrom, "-", false);
				}
				else
				{
					setPoNoFrom("");
					setPoNoTo("");
					LoadData("-", "-", printed);
				}
			},
				(error) => {
					console.log(error);
					showSnackBar(error.toString(), error?.response?.data?.errors[0], "error");
				}
			)
			.catch((error) => {
				console.log(error);
				showSnackBar(error.toString(), error?.response?.data?.errors[0], "error");
			});
	}

    return (
        <div className={style.PageMain}>
			<Title pageTitle="PO Filing Record" />
				<Paper variant="outlined" className={style.searchBar} >
					<div style={{ display: 'flex'}}>
						<div className={style.ModalFields} style={{width: '100%'}}>
							<TextField style={{ left: 10, width: 80, paddingTop: "5px", paddingBottom: "5px" }}
								onChange={(e) => {RegionChanged(e.target.value)}}
								value = {selectedRegion}
								select
								label="Region"
							>
								{regionComboData.map((item)=> (
									<MenuItem key={item.value} value={item.value}>
										{item.value}
									</MenuItem>
								))}
							</TextField>
							<div style={{ width: 10 }}></div>
							<TextField style={{ left: 10, width: 80, paddingTop: "5px", paddingBottom: "5px" }} 
								label="File No."
								value={fileNo}
								InputProps={{
									readOnly: true,
								}}
							></TextField>
							<div style={{ width: 10 }}></div>
							<TextField style={{ left: 10, width: 150, paddingTop: "5px", paddingBottom: "5px" }} 
								label="PO No."
								value={poNoFrom}
								onChange={(e) => {
									if (e.target.value.length > 5)
									{
										let poRegion = e.target.value.substring(4,6);
										setSelectedRegion(poRegion);
										setFileNo(branchInfo.find(x => x.region == poRegion)?.printIndex ?? 0);
									}
									setPoNoFrom(e.target.value);
								}}
							></TextField>
							<div style={{ width: 20 }}></div>
							<div><p style={{ textAlign: "center"}}>-</p></div>
							<TextField style={{ left: 10, width: 150, paddingTop: "5px", paddingBottom: "5px" }} 
								label="PO No."
								value={poNoTo}
								onChange={(e) => {
									setPoNoTo(e.target.value);
								}}
							></TextField>
							<div style={{ width: 20 }}></div>
							<div style={{ width: '100px', paddingTop: "18px", paddingLeft: "10px" }}>
								<FormControlLabel
									style={checkboxLabelStyle}
									control={
										<Checkbox 
											checked={printed} 
											onChange={(e) => {
												setPrinted(!printed);
												setGridData([]);
												if (printed)
												{
													setFileNo(branchInfo.find(x => x.region == selectedRegion)?.printIndex ?? 0);
													setSelectionModel([]);
												}
											}} 
										/>
									}
									label="Printed"
								/>
							</div>
							<div style={{ flexGrow: 1 }}></div>
							<div style={{ width: '100px', paddingTop: "12px", paddingLeft: "8px" }}>
								<Button type="submit" aria-controls="filter-menu" aria-haspopup="true" variant="contained" color="secondary" fullWidth onClick={(e)=> LoadData(poNoFrom, poNoTo, printed)}>
									Search
								</Button>
							</div>
							<div style={{ width: 10 }}></div>					

						</div>
					</div>
				</Paper>
				<div className={style.PageContent}>
					<DataGridPro
						rows={gridData}
						columns={columns}
						pageSize={searchData.pageSize}
						onPageSizeChange={(pageSize) => setSearchData({ ...searchData, pageSize: pageSize })}
						onPageChange={(pageIndex) => setCurrentPage(pageIndex)}
						pagination
						rowsPerPageOptions={[25, 50, 75]}
						loading={loading}
						checkboxSelection={printed}
						components={{
							Toolbar: CustomToolbar,
							Footer: CustomTablePagination
						}}
						componentsProps={{
							footer: { rowsPerPageOptions: [25, 50, 75] }
						}}
						selectionModel = {selectionModel}
						onSelectionModelChange={(selections) => {
							setSelectionModel(selections);
							const ids = new Set(selections);
							const selectedRows = gridData.filter((row) => ids.has(row.id));
							selectedData.current = selectedRows;
						}}
						headerHeight={tinyScreen ? 30 : (smallScreen ? 34 : 38)}
						rowHeight={tinyScreen ? 22 : (smallScreen ? 28 : 34)}
					/>
			</div>

			<PurchaseOrderFilingListViewer ref={viewerModalRef} onClose={()=>{
				RegionChanged(selectedRegion);
			}}/>

			<SnackBarMessage {...snackSettings} />

        </div>
    );
}

